import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineMail,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import { RiMessage2Line, RiChatHeartLine } from "react-icons/ri";
import { FiSend, FiLoader, FiX } from "react-icons/fi";
import contactImg from "../assets/contactus.jpg";

const Contact = () => {
  // 1. Form & Toast State
  const [formData, setFormData] = useState({
    full_name: "",
    mobile_number: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, title: "", message: "" });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 2. Handle Inputs
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 3. Show Toast Helper
  const triggerToast = (title, message) => {
    setToast({ show: true, title, message });
    setTimeout(() => setToast({ ...toast, show: false }), 5000); // Auto close after 5s
  };

  // 4. API Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new URLSearchParams();
      payload.append("full_name", formData.full_name);
      payload.append("mobile_number", formData.mobile_number);
      payload.append("email", formData.email);
      payload.append("message", formData.message);

      const response = await fetch("https://eveya.in/api/send_email", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload,
      });

      const result = await response.json();

      if (result.status === true) {
        triggerToast(
          "MESSAGE SENT",
          "Your message has been delivered successfully.",
        );
        setFormData({
          full_name: "",
          mobile_number: "",
          email: "",
          message: "",
        });
      } else {
        triggerToast("ERROR", "Something went wrong. Please try again.");
      }
    } catch (error) {
      triggerToast("SERVER ERROR", "Could not connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden py-20 px-6 bg-[#FFF0F3]">
      {/* --- CUSTOM TOAST NOTIFICATION --- */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ y: -100, x: "-50%", opacity: 0 }}
            animate={{ y: 40, x: "-50%", opacity: 1 }}
            exit={{ y: -100, x: "-50%", opacity: 0 }}
            className="fixed top-0 left-1/2 z-[100] w-[90%] max-w-md"
          >
            <div className="bg-white rounded-[2rem] shadow-2xl shadow-rose-200 flex items-center overflow-hidden border border-rose-50">
              {/* Left Icon Square */}
              <div className="p-3 pl-4">
                <div className="bg-rose-500 w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-200">
                  <HiOutlineCheckCircle className="text-white text-2xl" />
                </div>
              </div>

              {/* Text Area */}
              <div className="flex-1 px-3 py-4">
                <h4 className="text-rose-950 font-black text-xs uppercase tracking-widest">
                  {toast.title}
                </h4>
                <p className="text-rose-900/40 text-[11px] font-bold leading-tight mt-0.5">
                  {toast.message}
                </p>
              </div>

              {/* Divider */}
              <div className="w-[1px] h-10 bg-gray-100" />

              {/* Close Button */}
              <button
                onClick={() => setToast({ ...toast, show: false })}
                className="px-6 h-full text-rose-500 font-bold text-sm hover:bg-rose-50 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-rose-200 rounded-full blur-[100px]"
        />
      </div>

      {/* Main Glassmorphic Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-6xl bg-white/40 backdrop-blur-[30px] border border-white/40 rounded-[3rem] shadow-2xl overflow-hidden flex flex-col md:flex-row"
      >
        {/* Left Side */}
        <div className="md:w-5/12 bg-rose-500/10 p-12 flex flex-col justify-between border-r border-white/20">
          <div>
            <div className="flex items-center gap-2 text-rose-600 font-bold mb-6 tracking-tighter text-xl">
              <RiChatHeartLine size={28} />
              <span>EVEYA CARE</span>
            </div>
            <h1 className="text-4xl font-black text-rose-950 leading-tight mb-4 uppercase tracking-tighter">
              We're here <br /> for you.
            </h1>
            <p className="text-rose-900/60 font-medium">
              Have questions about your subscription? Our care team is
              listening.
            </p>
          </div>
          <img
            src={contactImg}
            alt="Care"
            className="w-full h-72 object-cover rounded-[2rem] shadow-lg grayscale-[20%]"
          />
        </div>

        {/* Right Side: Form */}
        <div className="md:w-7/12 p-12 md:p-16">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="relative border-b-2 border-rose-900/10 focus-within:border-rose-500 transition-all py-2">
              <label className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest block mb-1">
                Full Name
              </label>
              <input
                required
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                type="text"
                placeholder="Your Name"
                className="w-full bg-transparent outline-none text-rose-950 text-lg font-medium"
              />
              <HiOutlineUser
                className="absolute right-0 bottom-3 text-rose-900/30"
                size={22}
              />
            </div>

            <div className="relative border-b-2 border-rose-900/10 focus-within:border-rose-500 transition-all py-2">
              <label className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest block mb-1">
                Mobile Number
              </label>
              <input
                required
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                type="tel"
                placeholder="Mobile"
                className="w-full bg-transparent outline-none text-rose-950 text-lg font-medium"
              />
              <HiOutlinePhone
                className="absolute right-0 bottom-3 text-rose-900/30"
                size={22}
              />
            </div>

            <div className="relative border-b-2 border-rose-900/10 focus-within:border-rose-500 transition-all py-2">
              <label className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest block mb-1">
                Email
              </label>
              <input
                required
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                placeholder="Email"
                className="w-full bg-transparent outline-none text-rose-950 text-lg font-medium"
              />
              <HiOutlineMail
                className="absolute right-0 bottom-3 text-rose-900/30"
                size={22}
              />
            </div>

            <div className="relative border-b-2 border-rose-900/10 focus-within:border-rose-500 transition-all py-2">
              <label className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest block mb-1">
                Message
              </label>
              <textarea
                required
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="3"
                placeholder="Message"
                className="w-full bg-transparent outline-none text-rose-950 text-lg font-medium resize-none"
              />
              <RiMessage2Line
                className="absolute right-0 bottom-3 text-rose-900/30"
                size={22}
              />
            </div>

            <motion.button
              disabled={loading}
              whileHover={{ scale: 1.02, backgroundColor: "#E11D48" }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-rose-500 text-white py-5 rounded-2xl font-bold text-lg shadow-xl shadow-rose-200 flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <>
                  Send Message <FiSend />
                </>
              )}
            </motion.button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;
